import { getAuthHeaders } from "./client.js";
import { platformUrl } from "@/lib/api-config";

export interface GoogleDriveStatus {
  enabled: boolean;
  connected: boolean;
  reconnectRequired: boolean;
  connectedAccountEmail: string | null;
  connectedAt: string | null;
}

export interface GoogleDrivePickerConfig {
  clientId: string;
  developerKey: string;
  appId: string;
  accessToken: string;
}

export class GoogleDriveReconnectRequiredApiError extends Error {
  readonly reconnectRequired = true;

  constructor(message: string) {
    super(message);
    this.name = "GoogleDriveReconnectRequiredApiError";
  }
}

export function isGoogleDriveReconnectRequiredError(error: unknown): boolean {
  return (
    error instanceof GoogleDriveReconnectRequiredApiError ||
    (error instanceof Error && error.message.includes("authorization has expired"))
  );
}

async function platformFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(platformUrl(path), {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
      ...options.headers,
    },
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      typeof data === "object" && data && "error" in data
        ? String((data as { error: string }).error)
        : `Request failed (${response.status})`;

    if (
      typeof data === "object" &&
      data &&
      "reconnectRequired" in data &&
      (data as { reconnectRequired?: boolean }).reconnectRequired
    ) {
      throw new GoogleDriveReconnectRequiredApiError(message);
    }

    throw new Error(message);
  }

  return data as T;
}

export function getGoogleDriveStatus(): Promise<GoogleDriveStatus> {
  return platformFetch("/api/integrations/google-drive/status");
}

export function startGoogleDriveConnect(returnPath?: string): Promise<{ url: string }> {
  return platformFetch("/api/integrations/google-drive/connect", {
    method: "POST",
    body: JSON.stringify(returnPath ? { returnPath } : {}),
  });
}

export function disconnectGoogleDrive(): Promise<{ success: boolean }> {
  return platformFetch("/api/integrations/google-drive", { method: "DELETE" });
}

export interface StartGoogleDriveOnePickRequest {
  returnPath: string;
  mimeTypes?: string[];
  multiple?: boolean;
}

export function startGoogleDriveOnePick(
  request: StartGoogleDriveOnePickRequest
): Promise<{ url: string }> {
  return platformFetch("/api/integrations/google-drive/onepick/start", {
    method: "POST",
    body: JSON.stringify(request),
  });
}

export function getGoogleDrivePickerConfig(): Promise<GoogleDrivePickerConfig> {
  return platformFetch("/api/integrations/google-drive/picker-config");
}

export interface GoogleDriveDownloadResponse {
  fileName: string;
  mimeType: string;
  data: string;
}

export interface GoogleDriveDownloadRequest {
  fileId: string;
  resourceKey?: string | null;
  accessToken?: string;
}

export function downloadGoogleDriveFile(
  request: GoogleDriveDownloadRequest
): Promise<GoogleDriveDownloadResponse> {
  return platformFetch("/api/integrations/google-drive/download", {
    method: "POST",
    body: JSON.stringify({
      fileId: request.fileId,
      resourceKey: request.resourceKey ?? undefined,
      accessToken: request.accessToken,
    }),
  });
}

export function googleDriveDownloadToFile(response: GoogleDriveDownloadResponse): File {
  const binary = atob(response.data);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new File([bytes], response.fileName, { type: response.mimeType });
}
